import { useEffect, ChangeEvent, Fragment, useState } from 'react'
import { useTranslation } from 'next-i18next'

import appConfig from '~/config/app'
import { customerRoleDataByUserType } from '~/shared/utils/userInfo.utils'

import { Select } from '~/components/Select'
import { UserSkill } from '~/types/user-info-types'
import { getUserSkillLabel } from '~/utils/helpers'
import useCurrentUser from '~/hooks/useCurrentUser'
import { UserField } from '~/types/user'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import userSettingsAnalytics from '~/analytics/userSettings/userSettingsAnalytics'
import SelectOptionCountries from '~/components/PanelSettingsForm/SelectOptionCountries'

import SettingsCurriculumOptions from '~/components/PanelSettingsForm/SettingsCurriculumOptions'
import { useGetCurriculumNameFromCurriculumCountryCode } from '~/curriculum/hooks/useGetCurriculumCountryCode'
import { useResolvedUsCurriculumState } from '~/curriculum/hooks/useResolvedUsCurriculumState'
import { CurriculumCountryCodes } from '~/curriculum/constants/curriculum.const'
import { selectCurriculumName } from '~/curriculum/slice/persistCurriculum.slice'

import { selectFields, setFieldValueActionCreator } from '../redux/settings.slice'

export function ProfileTab(): React.ReactElement {
  const { t } = useTranslation(['settings', 'common'])

  const user = useCurrentUser()
  const dispatch = useAppDispatch()

  const fields = useAppSelector(selectFields)
  const persistentCurriculumName = useAppSelector(selectCurriculumName)
  const fallbackCurriculum = useGetCurriculumNameFromCurriculumCountryCode(user?.curriculumCountryCode)
  const [isCountriesLoaded, setIsCountriesLoaded] = useState(false)

  const { state: usCurriculumState, isUnset } = useResolvedUsCurriculumState()
  // A pre-column US account gets '' so the select shows the placeholder; the save path then leaves the curriculum alone.
  const curriculum =
    user?.curriculumCountryCode === CurriculumCountryCodes.USA && isUnset
      ? ''
      : usCurriculumState?.curriculumName || persistentCurriculumName || fallbackCurriculum

  useEffect(() => {
    if (!user) return

    if (user.userType) {
      dispatch(setFieldValueActionCreator({ fieldName: UserField.TYPE, value: user.userType }))
    }
    if (user.skill) {
      dispatch(setFieldValueActionCreator({ fieldName: UserField.SKILL, value: user.skill }))
    }
    if (user.yearsInSchool) {
      dispatch(setFieldValueActionCreator({ fieldName: UserField.YEARS_IN_SCHOOL, value: user.yearsInSchool }))
    }
    if (user.gradeLevelId) {
      dispatch(setFieldValueActionCreator({ fieldName: UserField.GRADE_LEVEL_ID, value: user.gradeLevelId }))
    }
    if (user.country && isCountriesLoaded) {
      dispatch(setFieldValueActionCreator({ fieldName: UserField.COUNTRY, value: user.country }))
    }
    if (user.curriculumCountryCode) {
      dispatch(setFieldValueActionCreator({ fieldName: UserField.CURRICULUM, value: curriculum }))
    }
    if (user.phone) {
      dispatch(setFieldValueActionCreator({ fieldName: UserField.PHONE, value: user.phone }))
    }
  }, [dispatch, user, curriculum, isCountriesLoaded])

  const handleOnChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const { name, value } = event.target

    dispatch(setFieldValueActionCreator({ fieldName: name, value }))

    userSettingsAnalytics.changeInSelectOption(name)
  }

  return (
    <Fragment>
      <div className="flex items-center space-y-2 md:space-y-0">
        <div className="text-gray-davy flex-1 text-xs md:text-sm">{t('settings:userType')}</div>
        <div className="flex-1 w-full ml-1">
          <Select
            className="bg-purple-medium"
            iconColor="text-black"
            isFullWidth
            name={UserField.TYPE}
            onChange={handleOnChange}
            value={fields.userType}
          >
            <Fragment>
              {!user?.userType && <option value="">{t('settings:chooseType')}</option>}
              {customerRoleDataByUserType.map((role) => (
                <option key={role.type} value={role.type}>
                  {t(`common:userTypes.${role.desc}`)}
                </option>
              ))}
            </Fragment>
          </Select>
        </div>
      </div>

      <div className="flex items-center space-y-2 md:space-y-0">
        <div className="text-gray-davy flex-1 text-xs md:text-sm">{t('settings:userSkill')}</div>
        <div className="flex-1 w-full ml-1">
          <Select
            className="bg-purple-medium"
            iconColor="text-black"
            isFullWidth
            name={UserField.SKILL}
            onChange={handleOnChange}
            value={fields.skill}
          >
            <Fragment>
              {!user?.skill && <option value="">{t('settings:chooseSkill')}</option>}
              {Object.values(UserSkill).map((option) => {
                if (typeof option !== 'number') {
                  return null
                }

                return (
                  <option key={option} value={option}>
                    {getUserSkillLabel(option, t)}
                  </option>
                )
              })}
            </Fragment>
          </Select>
        </div>
      </div>

      <div className="flex md:flex-row items-center space-y-2 md:space-y-0">
        <div className="flex-1 text-gray-davy text-xs sm:text-sm">{t('settings:userCountry')}</div>
        <div className="flex-1 w-full">
          <Select
            className="bg-purple-medium"
            iconColor="text-black"
            isFullWidth
            name={UserField.COUNTRY}
            onChange={handleOnChange}
            value={fields.country}
          >
            <SelectOptionCountries setHasCountriesLoaded={setIsCountriesLoaded} userCountry={fields.country} />
          </Select>
        </div>
      </div>

      {!appConfig.isSchoolPlatform && (
        <div className="flex md:flex-row items-center space-y-2 md:space-y-0">
          <div className="flex-1 text-gray-davy text-xs sm:text-sm">{t('settings:userCurriculum')}</div>
          <div className="flex-1 w-full">
            <Select
              className="bg-purple-medium"
              iconColor="text-black"
              isFullWidth
              name={UserField.CURRICULUM}
              onChange={handleOnChange}
              value={fields.curriculum}
            >
              <SettingsCurriculumOptions chosenCurriculum={fields.curriculum} />
            </Select>
          </div>
        </div>
      )}
    </Fragment>
  )
}
